// Step 6.7.2 / AC164: single source of truth for catalog filter state.
//
// Both the top chips, the left-panel checkboxes, and the Power Buyer
// chip strip subscribe to this store and dispatch to the same actions.
// URL state is a serialization of the store, not a parallel source of
// truth. See docs/tesla-filter-research.md for the full pattern rationale.

import { create } from "zustand";
import type { ReadonlyURLSearchParams } from "next/navigation";

export type CategorySlug =
  | "rose"
  | "garden-spray-rose"
  | "hydrangea"
  | "specialty"
  | "filler-green";

export type SortOrder = "az" | "za" | "price-asc" | "price-desc";

export type CatalogFilters = {
  categories: string[];
  colors: string[];
  lengths: string[];
  price: string;
  sort: SortOrder;
};

type CatalogFilterStore = CatalogFilters & {
  toggleCategory: (slug: string) => void;
  toggleColor: (slug: string) => void;
  toggleLength: (cm: string) => void;
  setPrice: (tier: string) => void;
  setSort: (order: SortOrder) => void;
  clearAll: () => void;
  hydrateFromUrl: (params: ReadonlyURLSearchParams | URLSearchParams) => void;
};

const EMPTY: CatalogFilters = {
  categories: [],
  colors: [],
  lengths: [],
  price: "",
  sort: "az",
};

function toggleArray(arr: string[], value: string): string[] {
  return arr.includes(value)
    ? arr.filter((v) => v !== value)
    : [...arr, value];
}

export const useCatalogFilters = create<CatalogFilterStore>((set) => ({
  ...EMPTY,
  toggleCategory: (slug) =>
    set((s) => ({ categories: toggleArray(s.categories, slug) })),
  toggleColor: (slug) =>
    set((s) => ({ colors: toggleArray(s.colors, slug) })),
  toggleLength: (cm) =>
    set((s) => ({ lengths: toggleArray(s.lengths, cm) })),
  setPrice: (tier) => set({ price: tier }),
  setSort: (order) => set({ sort: order }),
  clearAll: () => set({ ...EMPTY }),
  hydrateFromUrl: (params) => set(filtersFromSearchParams(params)),
}));

// The one URL -> filters parser (hydration + the sync hook's "is the URL
// already up to date?" check share it, so they can never disagree).
export function filtersFromSearchParams(
  params: ReadonlyURLSearchParams | URLSearchParams,
): CatalogFilters {
  return {
    categories: params.getAll("category"),
    colors: params.getAll("color"),
    lengths: params.getAll("length"),
    price: params.get("price") ?? "",
    sort: (params.get("sort") as SortOrder | null) ?? "az",
  };
}

// Just the filter fields of the store (drops the action functions).
export function pickFilters(s: CatalogFilters): CatalogFilters {
  return {
    categories: s.categories,
    colors: s.colors,
    lengths: s.lengths,
    price: s.price,
    sort: s.sort,
  };
}

// Serializes the store to a URLSearchParams instance, preserving any
// non-filter params (like ?view=power-buyer) that the caller passes in.
export function filtersToSearchParams(
  filters: CatalogFilters,
  preserve?: URLSearchParams,
): URLSearchParams {
  const params = new URLSearchParams();
  // Preserve non-filter params first (so view + q + any other ad-hoc
  // keys survive). q (search) is owned by SearchBar, not the filter
  // store - preserve it so a filter toggle doesn't drop the query.
  if (preserve) {
    const FILTER_KEYS = new Set([
      "category",
      "color",
      "length",
      "price",
      "sort",
    ]);
    preserve.forEach((value, key) => {
      if (!FILTER_KEYS.has(key)) params.append(key, value);
    });
  }
  filters.categories.forEach((c) => params.append("category", c));
  filters.colors.forEach((c) => params.append("color", c));
  filters.lengths.forEach((l) => params.append("length", l));
  if (filters.price) params.set("price", filters.price);
  if (filters.sort && filters.sort !== "az") params.set("sort", filters.sort);
  return params;
}

// Convenience selector for the visible-state predicates that components
// commonly need (avoids subscribing to the whole store when you only
// care about whether anything is active).
export function hasAnyFilter(s: CatalogFilters): boolean {
  return (
    s.categories.length > 0 ||
    s.colors.length > 0 ||
    s.lengths.length > 0 ||
    !!s.price ||
    s.sort !== "az"
  );
}
